'use client';

export type SeenFeedIds = {
  policyIds: number[];
  hometownNewsIds: number[];
  homeDonationBusinessIds: number[];
  productNumbers: string[];
  localMarketProducts: string[];
};

type FeedContent = {
  feedContentType?: string;
  contentId?: unknown;
  productList?: { productId?: unknown }[];
  customPolicyList?: { contentId?: unknown }[];
};

type SeenFeedIdsInput = Partial<Record<keyof SeenFeedIds, unknown>>;

const SEEN_FEED_IDS_EMPTY: SeenFeedIds = {
  policyIds: [],
  hometownNewsIds: [],
  homeDonationBusinessIds: [],
  productNumbers: [],
  localMarketProducts: [],
};

const createEmptySeenFeedIds = (): SeenFeedIds => ({
  policyIds: [],
  hometownNewsIds: [],
  homeDonationBusinessIds: [],
  productNumbers: [],
  localMarketProducts: [],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPositiveInteger = (value: unknown): number | undefined => {
  if (
    typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value > 0
  ) {
    return value;
  }

  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;

  const parsed = Number(trimmed);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const toStringId = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed || undefined;
  }

  if (
    typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value > 0
  ) {
    return String(value);
  }
};

const unique = <T>(values: T[]): T[] => [...new Set(values)];

const normalizeNumberIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];

  return unique(
    value
      .map(toPositiveInteger)
      .filter((id): id is number => id !== undefined),
  );
};

const normalizeStringIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return unique(
    value.map(toStringId).filter((id): id is string => id !== undefined),
  );
};

const removeStorageItem = (storage: Storage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage errors
  }
};

export function normalizeSeenFeedIds(value: unknown): SeenFeedIds | undefined {
  if (!isRecord(value)) return undefined;

  const input = value as SeenFeedIdsInput;

  return {
    policyIds: normalizeNumberIds(input.policyIds),
    hometownNewsIds: normalizeNumberIds(input.hometownNewsIds),
    homeDonationBusinessIds: normalizeNumberIds(
      input.homeDonationBusinessIds,
    ),
    productNumbers: normalizeStringIds(input.productNumbers),
    localMarketProducts: normalizeStringIds(input.localMarketProducts),
  };
}

export function loadSeenFeedIdsFromStorage(
  storage: Storage,
  key: string,
): SeenFeedIds | undefined {
  try {
    const raw = storage.getItem(key);
    if (!raw) return undefined;

    const normalized = normalizeSeenFeedIds(JSON.parse(raw));
    if (!normalized) removeStorageItem(storage, key);

    return normalized;
  } catch {
    removeStorageItem(storage, key);

    return undefined;
  }
}

export function saveSeenFeedIdsToStorage(
  storage: Storage,
  key: string,
  ids: SeenFeedIds,
) {
  try {
    storage.setItem(
      key,
      JSON.stringify(normalizeSeenFeedIds(ids) ?? SEEN_FEED_IDS_EMPTY),
    );
  } catch {
    // ignore storage errors
  }
}

export function loadSeenFeedIds(key: string): SeenFeedIds {
  return (
    loadSeenFeedIdsFromStorage(localStorage, key) ?? createEmptySeenFeedIds()
  );
}

export function saveSeenFeedIds(key: string, ids: SeenFeedIds) {
  saveSeenFeedIdsToStorage(localStorage, key, ids);
}

export function extractSeenIds(contents: FeedContent[]): SeenFeedIds {
  const seenIds = createEmptySeenFeedIds();

  contents.forEach((content) => {
    if (content.feedContentType === 'POLICY') {
      seenIds.policyIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'CUSTOM_POLICY') {
      seenIds.policyIds.push(...normalizeNumberIds([content.contentId]));
    }

    if (content.feedContentType === 'HOMETOWN_NEWS') {
      seenIds.hometownNewsIds.push(
        ...normalizeNumberIds([content.contentId]),
      );
    }

    if (content.feedContentType === 'DONATION') {
      seenIds.homeDonationBusinessIds.push(
        ...normalizeNumberIds([content.contentId]),
      );
    }

    if (content.feedContentType === 'HOME_PRODUCT') {
      seenIds.productNumbers.push(
        ...normalizeStringIds(
          content.productList?.map((product) => product.productId),
        ),
      );
    }

    if (content.feedContentType === 'LOCAL_MARKET') {
      seenIds.localMarketProducts.push(
        ...normalizeStringIds(
          content.productList?.map((product) => product.productId),
        ),
      );
    }

    seenIds.policyIds.push(
      ...normalizeNumberIds(
        content.customPolicyList?.map((policy) => policy.contentId),
      ),
    );
  });

  return normalizeSeenFeedIds(seenIds) ?? createEmptySeenFeedIds();
}

export function mergeSeenIds(...seenIdsList: SeenFeedIds[]): SeenFeedIds {
  const merged = createEmptySeenFeedIds();

  seenIdsList.forEach((seenIds) => {
    merged.policyIds.push(...seenIds.policyIds);
    merged.hometownNewsIds.push(...seenIds.hometownNewsIds);
    merged.homeDonationBusinessIds.push(...seenIds.homeDonationBusinessIds);
    merged.productNumbers.push(...seenIds.productNumbers);
    merged.localMarketProducts.push(...seenIds.localMarketProducts);
  });

  return normalizeSeenFeedIds(merged) ?? createEmptySeenFeedIds();
}

export function toBodyFilter(ids: SeenFeedIds): Partial<SeenFeedIds> {
  const normalized = normalizeSeenFeedIds(ids) ?? createEmptySeenFeedIds();

  return {
    ...(normalized.policyIds.length > 0 && {
      policyIds: normalized.policyIds,
    }),
    ...(normalized.hometownNewsIds.length > 0 && {
      hometownNewsIds: normalized.hometownNewsIds,
    }),
    ...(normalized.homeDonationBusinessIds.length > 0 && {
      homeDonationBusinessIds: normalized.homeDonationBusinessIds,
    }),
    ...(normalized.productNumbers.length > 0 && {
      productNumbers: normalized.productNumbers,
    }),
    ...(normalized.localMarketProducts.length > 0 && {
      localMarketProducts: normalized.localMarketProducts,
    }),
  };
}
